import React, { useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { themeQuartz } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import images from "../../../assets/images";
import { Trash2 } from "lucide-react";
import { ContractorVendorManagement as ContractorVendorManagementType } from "../../../types/api";
import { useContractorVendorManagementStore } from "../../../store/store";
import { toast } from "react-toastify";
import CreateContractorVendorManagement from "./CreateContractorVendorManagement";

const gridTheme = themeQuartz.withParams({
  accentColor: "#93AA9A",
  headerHeight: "200px",
  browserColorScheme: "light",
  headerFontSize: 14,
});

const ContractorVendorManagement: React.FC = () => {
  const [rowData, setRowData] = useState<ContractorVendorManagementType[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState<"create" | "edit" | "view">("create");
  const [selectedData, setSelectedData] =
    useState<ContractorVendorManagementType | null>(null);

  const {
    contractorVendors,
    fetchContractorVendors,
    createContractorVendor,
    updateContractorVendor,
    deleteContractorVendor,
    loading,
  } = useContractorVendorManagementStore();

  useEffect(() => {
    fetchContractorVendors().catch(() =>
      toast.error("Failed to load contractor & vendor records")
    );
  }, [fetchContractorVendors]);

  useEffect(() => {
    if (Array.isArray(contractorVendors)) {
      setRowData(contractorVendors);
    } else {
      setRowData([]);
    }
  }, [contractorVendors]);

  const handleView = (data: ContractorVendorManagementType) => {
    setSelectedData(data);
    setMode("view");
    setIsModalOpen(true);
  };

  const handleEdit = (data: ContractorVendorManagementType) => {
    setSelectedData(data);
    setMode("edit");
    setIsModalOpen(true);
  };

  const handleDelete = async (id?: string) => {
    if (!id) return;
    await deleteContractorVendor(id);
  };

  const [columnDefs] = useState<any[]>([
    // {
    //   field: "venderId",
    //   headerName: "Vendor ID",
    //   sortable: false,
    //   minWidth: 120,
    //   flex: 0.6,
    //   headerStyle: { backgroundColor: "#e9f5ff" },
    //   filter: false,
    //   cellStyle: { padding: "10px 0 0 5px " },
    // },
    {
      field: "vendorName",
      headerName: "Vendor Name",
      sortable: false,
      minWidth: 150,
      flex: 1.2,
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      filter: false,
      cellStyle: { padding: "10px 0 0 5px " },
    },
    {
      field: "phoneNumber",
      headerName: "Phone Number",
      sortable: false,
      minWidth: 150,
      flex: 0.8,
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      filter: false,
      cellStyle: { padding: "10px 0 0 5px " },
    },
    {
      field: "email",
      headerName: "Email",
      sortable: false,
      minWidth: 200,
      flex: 1.5,
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      filter: false,
      cellStyle: { padding: "10px 0 0 5px " },
    },
    {
      field: "timelines",
      headerName: "Timelines",
      sortable: false,
      minWidth: 100,
      flex: 0.5,
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      filter: false,
      cellStyle: { padding: "10px 0 0 5px " },
    },
    {
      field: "compliance",
      headerName: "Compliance Status",
      sortable: false,
      minWidth: 150,
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      filter: false,
      cellStyle: { padding: "10px 0 0 5px " },
      cellRenderer: (params: any) => (
        <div
          className={`${params.data.compliance === "Compliant"
              ? "text-green-500 font-bold"
              : params.data.compliance === "Non-Compliant"
                ? "text-red-500  font-bold"
                : "text-yellow-500  font-bold"
            } px-2 py-1 text-md`}
        >
          {params.data.compliance}
        </div>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      cellStyle: { padding: "10px 0 0 0 " },
      minWidth: 120,
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" as const },
      flex: 0.8,
      cellRenderer: (params: any) => (
        <div className="flex gap-x-3 pt-2">
          <img
            onClick={() => handleView(params.data)}
            className="iconTd cursor-pointer"
            src={images.viewIcon}
            alt="View"
          />
          <img
            onClick={() => handleEdit(params.data)}
            className="iconTd cursor-pointer"
            src={images.editIcon}
            alt="Edit"
          />
          <Trash2
            onClick={() => handleDelete(params.data._id)}
            className="cursor-pointer text-red-500"
          />
        </div>
      ),
      filter: false,
    },
  ]);

  const theme = useMemo(() => {
    return gridTheme;
  }, []);

  const defaultColDef = useMemo(
    () => ({
      wrapText: true,
      headerClass: "font-bold text-md bg-gray-100 p-2",
    }),
    []
  );

  return (
    <>
      <div className="px-2 py-4 mt-14 w-full h-full">
        <div className="bg-white rounded-lg shadow py-6 px-2">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-900">
              Contractor & Vendor Management
            </h1>
            <button
              onClick={() => {
                setSelectedData(null);
                setIsModalOpen(true);
                setMode("create");
              }}
              className="flex btn-primary roundedBt iconWithBt text-uppercase mr-4"
            >
              ADD CONTRACTOR / VENDOR
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div
              className="ag-theme-alpine mt-4 w-full"
              style={{ height: "600px", width: "100%" }}
            >
              <AgGridReact
                rowData={rowData}
                columnDefs={columnDefs}
                theme={theme}
                headerHeight={65}
                rowHeight={60}
                defaultColDef={defaultColDef}
              />
            </div>
          )}
        </div>
      </div>

      <CreateContractorVendorManagement
        isOpen={isModalOpen}
        mode={mode}
        initialData={selectedData}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedData(null);
        }}
        onSubmit={async (data) => {
          if (mode === "create") {
            await createContractorVendor(data);
          } else if (mode === "edit" && data._id) {
            await updateContractorVendor(data._id, data);
          }
          setIsModalOpen(false);
          setSelectedData(null);
        }}
      />
    </>
  );
};

export default ContractorVendorManagement;
